import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AlertType, Category, Error } from '../types/common';
import { CREATE_CATEGORY_ERROR_MESSAGE, DELETE_CATEGORY_ERROR_MESSAGE, FETCH_CATEGORIES_ERROR_MESSAGE, UPDATE_CATEGORY_ERROR_MESSAGE } from '../constants/messages';
import { showAlert } from './CommonSlice';
import { getCsrfToken } from '../utils/helpers';

export type CategoryWithSEO = Category & { title?: string };

export type CategoryState = {
    categories: CategoryWithSEO[];
    selectedCategory: CategoryWithSEO;
    isLoading: boolean;
    error: Error;
};

const initialState: CategoryState = {
    categories: [],
    selectedCategory: {
        id: '',
        name: '',
        description: '',
        url: '',
        title: '',
    },
    isLoading: false,
    error: {
        isError: false,
        message: '',
    },
};

const BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';

// Fetch categories
export const fetchCategories = createAsyncThunk('category/fetchCategories', async (_, { dispatch, rejectWithValue }) => {
    const response = await fetch(`${BASE_URL}/categories`);

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: FETCH_CATEGORIES_ERROR_MESSAGE }));
        return rejectWithValue(FETCH_CATEGORIES_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id` для совместимости с компонентом
    const categories: Category[] = data.map((category: any) => ({
        id: category._id, // Преобразование _id в id
        name: category.name,
        description: category.description,
        url: category.url,
        title: category.title ?? '',
    }));

    return categories;
});

// Create category
export const createCategory = createAsyncThunk('category/createCategory', async (category: Partial<CategoryWithSEO>, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/categories`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify(category),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: CREATE_CATEGORY_ERROR_MESSAGE }));
        return rejectWithValue(CREATE_CATEGORY_ERROR_MESSAGE);
    }

    const data = await response.json();

    // Преобразуем `_id` в `id`
    return { id: data.id, ...category } as CategoryWithSEO;
});

// Update category
export const updateCategory = createAsyncThunk('category/updateCategory', async (category: CategoryWithSEO, { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/categories/${category.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
        body: JSON.stringify({
            name: category.name,
            description: category.description,
            url: category.url,
            title: category.title ?? '',
        }),
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: UPDATE_CATEGORY_ERROR_MESSAGE }));
        return rejectWithValue(UPDATE_CATEGORY_ERROR_MESSAGE);
    }

    const data = await response.json();

    const updatedCategory: CategoryWithSEO = {
        id: category.id, // Сохраняем оригинальный `id`
        ...data,
    };

    return updatedCategory;
});

// Delete category
export const deleteCategory = createAsyncThunk('category/deleteCategory', async (id: Category['id'], { dispatch, rejectWithValue }) => {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${BASE_URL}/categories/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-type': 'application/json',
            'csrf-token': csrfToken,
        },
    });

    if (!response.ok) {
        dispatch(showAlert({ type: AlertType.Error, message: DELETE_CATEGORY_ERROR_MESSAGE }));
        return rejectWithValue(DELETE_CATEGORY_ERROR_MESSAGE);
    }

    return id;
});

export const categorySlice = createSlice({
    name: 'category',
    initialState,
    reducers: {
        selectCategory: (state, action: PayloadAction<Category['id']>) => {
            const category = state.categories.find((category) => category.id === action.payload);

            if (!category) return;

            state.selectedCategory = category;
        },

        removeSelectedCategory: (state) => {
            state.selectedCategory = initialState.selectedCategory;
        },
    },

    extraReducers: (builder) => {
        builder.addCase(fetchCategories.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(fetchCategories.fulfilled, (state, { payload }) => {
            state.categories = payload;
            state.isLoading = false;
        });

        builder.addCase(fetchCategories.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(createCategory.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(createCategory.fulfilled, (state, { payload }) => {
            state.categories.push(payload as CategoryWithSEO);
            state.isLoading = false;
        });

        builder.addCase(createCategory.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(updateCategory.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(updateCategory.fulfilled, (state, { payload }) => {
            const index = state.categories.findIndex((category) => category.id === (payload as CategoryWithSEO).id);
            if (index < 0) return;
            state.categories[index] = payload as CategoryWithSEO;
            state.isLoading = false;
        });

        builder.addCase(updateCategory.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });

        builder.addCase(deleteCategory.pending, (state) => {
            state.isLoading = true;
        });

        builder.addCase(deleteCategory.fulfilled, (state, { payload }) => {
            const index = state.categories.findIndex((category) => category.id === payload);
            state.categories.splice(index, 1);
            state.isLoading = false;
        });

        builder.addCase(deleteCategory.rejected, (state, { payload }) => {
            state.error = {
                isError: true,
                message: payload as Error['message'],
            };
            state.isLoading = false;
        });
    },
});

export const { selectCategory, removeSelectedCategory } = categorySlice.actions;

export default categorySlice.reducer;
